<script lang="ts" setup>
import AuthAPI from '~/api/auth';
import type { ResponseError } from '~/api/utils/types';

defineProps<{
  withTitle?: boolean;
}>();
const emit = defineEmits<{
  (event: 'submit'): void;
  (event: 'toEmail'): void;
}>();
const authorizationTheme = inject(
  INJECTION_KEYS.AuthorizationTheme,
  ThemeEnum.dark,
);

const { t } = useI18n();

const error = ref<string>();
const email = ref('');

const { execute, isValid, isLoading, parameters } = useRequest(
  AuthAPI.forgotPassword,
  () => ({ body: { email: email.value } }),
);

const onSubmit = () =>
  execute()
    ?.then(() => {
      emit('submit');
    })
    .catch((e: ResponseError) => {
      error.value = getFetchErrorMessage(e, t('error_message'));
    });

watch(parameters, () => {
  error.value = undefined;
});
</script>

<template>
  <form @submit.prevent="onSubmit">
    <h2 v-if="withTitle" class="authorizationComponent__title">
      {{ t('title') }}
    </h2>
    <div class="authorizationComponent__fields">
      <div>
        <BaseTextField
          v-model:value="email"
          type="email"
          autocomplete="email"
          inputmode="email"
          name="email"
          :placeholder="t('email_placeholder')"
          :bordered="authorizationTheme === ThemeEnum.light"
          :failed="!!error"
          class="authorizationComponent__input"
        />
        <BaseCollapse :visible="!!error" :fixed-duration="150">
          <span class="authorizationComponent__errorMessage">{{ error }}</span>
        </BaseCollapse>
      </div>
    </div>

    <BaseButton
      :disabled="!isValid"
      :loading="isLoading"
      type="submit"
      class="authorizationComponent__submit"
      :dark="authorizationTheme === ThemeEnum.light"
    >
      {{ t('action') }}
    </BaseButton>

    <BaseButton
      @click="emit('toEmail')"
      class="authorizationComponent__back"
      :dark="authorizationTheme === ThemeEnum.light"
      outlined
    >
      {{ t('back') }}
    </BaseButton>
  </form>
</template>

<i18n>
ru:
  title: Восстановление пароля
  email_placeholder: E-mail
  error_message: При отправке письма для восстановления пароля произошла ошибка
  action: Восстановить
  back: Назад

en:
  title: Password recovery
  email_placeholder: E-mail
  error_message: An error occurred while sending the password recovery email
  action: Recover
  back: Back
</i18n>
